import React, { useState, useEffect } from 'react';
import { Guide } from '../../../types/guide';
import { Organizer } from '../../../types/organizer';
import { useLanguage } from '../../../contexts/LanguageContext';

type Props = {
  onSubmit: (data: Partial<Guide>) => void;
  initialData?: Partial<Guide>;
  isLoading?: boolean;
  isEditing?: boolean;
  organizers?: Record<string, Organizer>;
  userRole?: string;
  userId?: string;
};

export const GuideForm = ({
  onSubmit,
  initialData = {},
  isLoading,
  isEditing,
  organizers = {},
  userRole = '',
  userId = ''
}: Props) => {
  const { translations } = useLanguage();

  const [formData, setFormData] = useState({
    name: initialData.name || '',
    email: initialData.email || '',
    password: '',
    confirmPassword: '',
    organizerId: userRole === 'organizer' ? userId : initialData.organizerId || '',
    passportNumber: initialData.passportNumber || '',
    nusukEmail: initialData.nusukEmail || '',
    mainPhone: initialData.mainPhone || '',
    hajjPhone: initialData.hajjPhone || '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // When email is changed and nusukEmail is empty, set nusukEmail to email
  useEffect(() => {
    if (formData.email && !formData.nusukEmail) {
      setFormData(prev => ({
        ...prev,
        nusukEmail: formData.email
      }));
    }
  }, [formData.email]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = translations?.guideForm?.errors?.name || 'Name is required';
    }

    if (!formData.organizerId) {
      newErrors.organizerId = translations?.guideForm?.errors?.organizerId || 'Organizer is required';
    }

    if (!isEditing) {
      if (!formData.email.trim()) {
        newErrors.email = translations?.guideForm?.errors?.emailRequired || 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = translations?.guideForm?.errors?.invalidEmail || 'Invalid email address';
      }

      if (!formData.password) {
        newErrors.password = translations?.guideForm?.errors?.passwordRequired || 'Password is required';
      } else if (formData.password.length < 6) {
        newErrors.password = translations?.guideForm?.errors?.passwordLength || 'Password must be at least 6 characters';
      }

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = translations?.guideForm?.errors?.passwordMismatch || 'Passwords do not match';
      }
    }

    // Validate phone numbers if provided
    if (formData.mainPhone) {
      const phoneRegex = /^\+[1-9]\d{1,14}$/;
      if (!phoneRegex.test(formData.mainPhone)) {
        newErrors.mainPhone = 'Phone must include country code (e.g., +966XXXXXXXX)';
      }
    }

    // Specifically validate Hajj phone to be a Saudi number
    if (formData.hajjPhone) {
      if (!formData.hajjPhone.startsWith('+966')) {
        newErrors.hajjPhone = 'Hajj phone must be a Saudi number starting with +966';
      } else if (!/^\+966\d{9}$/.test(formData.hajjPhone)) {
        newErrors.hajjPhone = 'Invalid Saudi number format (should be +966 followed by 9 digits)';
      }
    }

    // Validate nusuk email if provided
    if (formData.nusukEmail && !/\S+@\S+\.\S+/.test(formData.nusukEmail)) {
      newErrors.nusukEmail = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const { confirmPassword, ...submitData } = formData;
      onSubmit(submitData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {translations?.guideForm?.labels?.name || 'Name'}
          <span className="text-red-500 mr-1">*</span>
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className={`w-full px-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500 ${
            errors.name ? 'border-red-500' : 'border-gray-300'
          }`}
          required
        />
        {errors.name && (
          <p className="mt-1 text-sm text-red-500">{errors.name}</p>
        )}
      </div>

      {!isEditing && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {translations?.guideForm?.labels?.email || 'Email'}
              <span className="text-red-500 mr-1">*</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              className={`w-full px-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500 ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              required
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-500">{errors.email}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {translations?.guideForm?.labels?.password || 'Password'}
              <span className="text-red-500 mr-1">*</span>
            </label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
              className={`w-full px-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500 ${
                errors.password ? 'border-red-500' : 'border-gray-300'
              }`}
              required
              minLength={6}
            />
            {errors.password && (
              <p className="mt-1 text-sm text-red-500">{errors.password}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {translations?.guideForm?.labels?.confirmPassword ||
                'Confirm Password'}
              <span className="text-red-500 mr-1">*</span>
            </label>
            <input
              type="password"
              value={formData.confirmPassword}
              onChange={(e) =>
                setFormData({ ...formData, confirmPassword: e.target.value })
              }
              className={`w-full px-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500 ${
                errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
              }`}
              required
              minLength={6}
            />
            {errors.confirmPassword && (
              <p className="mt-1 text-sm text-red-500">
                {errors.confirmPassword}
              </p>
            )}
          </div>
        </>
      )}

      {/* Additional fields */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Passport Number <span className="text-xs text-gray-500">(optional)</span>
        </label>
        <input
          type="text"
          value={formData.passportNumber}
          onChange={(e) => setFormData({ ...formData, passportNumber: e.target.value })}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Nusuk Email <span className="text-xs text-gray-500">(optional)</span>
        </label>
        <input
          type="email"
          value={formData.nusukEmail}
          onChange={(e) => setFormData({ ...formData, nusukEmail: e.target.value })}
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
            errors.nusukEmail ? 'border-red-500' : 'border-gray-300'
          }`}
        />
        {errors.nusukEmail && (
          <p className="mt-1 text-sm text-red-500">{errors.nusukEmail}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Main Phone Number <span className="text-xs text-gray-500">(optional, with country code +xxx)</span>
        </label>
        <input
          type="tel"
          placeholder="+966XXXXXXXX"
          value={formData.mainPhone}
          onChange={(e) => setFormData({ ...formData, mainPhone: e.target.value })}
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
            errors.mainPhone ? 'border-red-500' : 'border-gray-300'
          }`}
        />
        {errors.mainPhone && (
          <p className="mt-1 text-sm text-red-500">{errors.mainPhone}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Hajj Phone Number <span className="text-xs text-gray-500">(optional, must be a Saudi number +966)</span>
        </label>
        <input
          type="tel"
          placeholder="+966XXXXXXXXX"
          value={formData.hajjPhone}
          onChange={(e) => setFormData({ ...formData, hajjPhone: e.target.value })}
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
            errors.hajjPhone ? 'border-red-500' : 'border-gray-300'
          }`}
        />
        {errors.hajjPhone && (
          <p className="mt-1 text-sm text-red-500">{errors.hajjPhone}</p>
        )}
      </div>

      {/* Organizer Selection */}
      {userRole === 'admin' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {translations?.guideForm?.labels?.organizer || 'Organizer'}
            <span className="text-red-500 mr-1">*</span>
          </label>
          <select
            value={formData.organizerId}
            onChange={(e) => setFormData({ ...formData, organizerId: e.target.value })}
            className={`w-full px-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500 ${
              errors.organizerId ? 'border-red-500' : 'border-gray-300'
            }`}
            required
          >
            <option value="">
              {translations?.guideForm?.selectOrganizer || 'Select Organizer'}
            </option>
            {Object.values(organizers).map((organizer) => (
              <option key={organizer.id} value={organizer.id}>
                {organizer.name}
              </option>
            ))}
          </select>
          {errors.organizerId && (
            <p className="mt-1 text-sm text-red-500">{errors.organizerId}</p>
          )}
        </div>
      )}

      <div className="flex justify-end gap-3 mt-6">
        <button
          type="submit"
          disabled={isLoading}
          className={`px-6 py-2 rounded-lg text-white transition-colors ${
            isLoading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-primary-500 hover:bg-primary-600'
          }`}
        >
          {isLoading
            ? translations?.guideForm?.loading || 'Saving...'
            : translations?.guideForm?.submit || 'Save'}
        </button>
      </div>
    </form>
  );
};
